import type { Platform, InboundACL } from "../lib/types";
import { cn } from "../lib/cn";

const PLATFORM_LABELS: Record<Platform, string> = {
  whatsapp: "WhatsApp",
  gchat: "Google Chat",
  telegram: "Telegram",
  asana: "Asana",
  email: "Email",
  slack: "Slack",
  internal: "Internal",
};

const PLATFORM_COLORS: Record<Platform, string> = {
  whatsapp: "text-platform-whatsapp",
  gchat: "text-platform-gchat",
  telegram: "text-platform-telegram",
  asana: "text-platform-asana",
  email: "text-platform-email",
  slack: "text-platform-slack",
  internal: "text-fg-muted",
};

const PLATFORM_SHORT: Record<Platform, string> = {
  whatsapp: "WA",
  gchat: "GC",
  telegram: "TG",
  asana: "AS",
  email: "EM",
  slack: "SL",
  internal: "IN",
};

/** Compact, legible platform marker: a colored dot + short code (WA/TG/GC).
 *  Replaces the bare dot, which forced you to memorise the colour map. */
export function PlatformTag({ platform }: { platform: Platform }) {
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-[10px] font-semibold", PLATFORM_COLORS[platform])}
      title={PLATFORM_LABELS[platform]}
    >
      <span className="size-1.5 rounded-full" style={{ backgroundColor: "currentColor" }} />
      {PLATFORM_SHORT[platform]}
    </span>
  );
}

export function PlatformBadge({ platform, compact }: { platform: Platform; compact?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[11px] font-medium",
        PLATFORM_COLORS[platform],
      )}
    >
      <span
        className="size-1.5 rounded-full"
        style={{ backgroundColor: "currentColor" }}
      />
      {!compact && PLATFORM_LABELS[platform]}
    </span>
  );
}

export function AclBadge({ acl }: { acl: InboundACL }) {
  const styles: Record<InboundACL, string> = {
    allow: "bg-success-subtle text-success border-success/30",
    deny: "bg-danger-subtle text-danger border-danger/30",
  };
  const labels: Record<InboundACL, string> = {
    allow: "allow",
    deny: "deny",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center h-5 px-1.5 rounded text-[11px] font-medium border",
        styles[acl],
      )}
    >
      {labels[acl]}
    </span>
  );
}

export function PersonaBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[11px] font-medium bg-info-subtle text-info border border-info/30">
      <span className="size-1 rounded-full bg-info" />
      {name}
    </span>
  );
}

/** A route's terminus, rendered consistently across sink kinds: persona keeps
 *  the familiar purple chip; notification/webhook/script get their own
 *  colour-coded chip of the SAME size, so they read as destinations, not as a
 *  heavier "kind" blob. */
export function SinkBadge({ sinkType, persona }: { sinkType?: string | null; persona?: string | null }) {
  const t = sinkType ?? "persona";
  if (t === "persona") return <PersonaBadge name={persona || "—"} />;
  const styles: Record<string, string> = {
    notification: "bg-warning-subtle text-warning border-warning/30",
    webhook: "bg-accent-subtle text-accent border-accent/30",
    script: "bg-success-subtle text-success border-success/30",
  };
  const icons: Record<string, string> = { notification: "📣", webhook: "🪝", script: "⚙" };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 h-5 px-1.5 rounded text-[11px] font-medium border",
        styles[t] ?? "bg-surface-2 text-fg-muted border-border",
      )}
    >
      <span className="leading-none">{icons[t] ?? "•"}</span>
      {t}
    </span>
  );
}

export function KindBadge({ kind }: { kind: string }) {
  // Neutral mono letter — kinds aren't statuses, don't color them as such.
  const letter = (kind?.[0] ?? "?").toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center size-5 rounded text-[10px] font-mono font-semibold bg-surface-2 text-fg-muted border border-border"
      title={`kind: ${kind}`}
    >
      {letter}
    </span>
  );
}
